import { forwardRef, useImperativeHandle, useRef } from 'react';

/**
 * 6 single-digit boxes. Typing advances, Backspace goes back, ←/→ move, and pasting
 * (or the OS one-time-code autofill) fills every box. Calls `onComplete(code)` once all
 * digits are filled. `value` is an array of single-character strings.
 * Ref exposes `focus(index = first empty)`.
 */
const OtpInput = forwardRef(function OtpInput(
  { value, onChange, onComplete, length = 6, disabled, invalid, describedBy, label = 'Security code' },
  ref,
) {
  const inputs = useRef([]);

  useImperativeHandle(ref, () => ({
    focus(index) {
      const target = index ?? Math.max(0, value.findIndex((d) => !d));
      inputs.current[target === -1 ? length - 1 : target]?.focus();
    },
  }));

  const commit = (next, focusIndex) => {
    onChange(next);
    if (focusIndex != null) inputs.current[Math.min(focusIndex, length - 1)]?.focus();
    if (next.every(Boolean)) onComplete?.(next.join(''));
  };

  const fillFrom = (start, digits) => {
    const next = [...value];
    digits.slice(0, length - start).forEach((d, i) => {
      next[start + i] = d;
    });
    commit(next, start + digits.length);
  };

  const handleChange = (index, raw) => {
    const digits = raw.replace(/\D/g, '').split('');
    if (digits.length === 0) {
      const next = [...value];
      next[index] = '';
      onChange(next);
      return;
    }
    // More than one digit at once = autofill or a fast typist: spread them out
    fillFrom(index, digits.length > 1 ? digits : [digits[digits.length - 1]]);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      e.preventDefault();
      const next = [...value];
      next[index - 1] = '';
      onChange(next);
      inputs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      inputs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (index, e) => {
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').split('');
    if (!digits.length) return;
    e.preventDefault();
    fillFrom(digits.length >= length ? 0 : index, digits);
  };

  return (
    <div role="group" aria-label={label} aria-describedby={describedBy} className="grid grid-cols-6 gap-2 sm:gap-2.5">
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={(el) => {
            inputs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={i === 0 ? length : 1}
          aria-label={`Digit ${i + 1} of ${length}`}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          value={value[i] ?? ''}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={(e) => handlePaste(i, e)}
          onFocus={(e) => e.target.select()}
          className={`w-full aspect-square max-h-14 rounded-xl border-2 text-center font-data-mono text-xl font-bold text-on-surface outline-none transition disabled:opacity-50 ${
            invalid
              ? 'border-error bg-status-alert-bg/50'
              : value[i]
                ? 'border-outline-variant/40 bg-surface-container-low'
                : 'border-transparent bg-surface-container-low'
          } focus:border-primary-container focus:bg-surface-container-lowest focus:shadow-[0_0_0_3px_rgba(34,197,94,0.15)]`}
        />
      ))}
    </div>
  );
});

export default OtpInput;
